import React, { useEffect, useMemo, useState } from 'react';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/solid';
import Modal from 'react-modal';
import DeleteConfirm from '../../../sharedComponents/DeleteConfirm';
import { toast } from 'react-toastify';
import useManageAdminMistakes from './useManageAdminMistakes';
import AdminMistakeService from './AdminMistakeService';
import AdminService from '../../setting/admin/AdminService';
import AdminKpiRecordService from '../kpiRecord/AdminKpiRecordService';

const customStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      height: 'auto',
      width: '500px',
      maxHeight: '90vh',
      overflowY: 'auto',
      overflowX: 'hidden',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
    },
  };

const emptyForm = () => ({
    id: '',
    orderId: '',
    orderUrl: '',
    adminId: '',
    adminKpiRecordId: '',
    actualMoneyLost: '',
    deductFromAdmin: '',
    reason: '',
    issueDate: new Date().toISOString().slice(0, 10),
    notes: ''
});

const AdminMistakeRecord = () => {
  const [mode, setMode] = useState('register');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [record, setRecord] = useState(emptyForm);
  const [admins, setAdmins] = useState([]);
  const [kpiOptions, setKpiOptions] = useState([]);
  const [orderResolveLoading, setOrderResolveLoading] = useState(false);
  const [orderResolveOptions, setOrderResolveOptions] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filterAdminId, setFilterAdminId] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  const {
    mistakes,
    isLoading,
    error,
    addMistake,
    editMistake,
    deleteMistake,
    refreshMistakes
  } = useManageAdminMistakes();

  useEffect(() => {
    const loadAdmins = async () => {
      try {
        const res = await AdminService.getAdmins();
        setAdmins(res.data);
      } catch (err) {
        console.error(err);
      }
    };
    loadAdmins();
  }, []);

  useEffect(() => {
    if (!record.adminId) {
      setKpiOptions([]);
      return undefined;
    }
    let cancelled = false;
    AdminKpiRecordService.getAdminKpiRecords({ adminId: record.adminId })
      .then((res) => {
        if (!cancelled) setKpiOptions(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setKpiOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [record.adminId]);

  const kpiSelectOptions = useMemo(() => {
    const sel = parseInt(record.adminKpiRecordId, 10);
    if (!record.adminKpiRecordId || Number.isNaN(sel)) return kpiOptions;
    if (kpiOptions.some((k) => k.id === sel)) return kpiOptions;
    return [
      ...kpiOptions,
      {
        id: sel,
        month: '—',
        year: '—',
        pointsDeducted: '—',
        kpi: { topic: 'Linked KPI (reload list if needed)', group: '' }
      }
    ];
  }, [kpiOptions, record.adminKpiRecordId]);

  const selectedOrderLabel = useMemo(() => {
    if (!record.orderId) return '—';
    const smile = selectedOrder?.smileNameInput || '';
    const code = selectedOrder?.orderCode || '';
    if (smile) return smile;
    if (code) return code;
    return `#${record.orderId}`;
  }, [record.orderId, selectedOrder]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterAdminId, filterDateFrom, filterDateTo]);

  const filteredRecords = mistakes.filter((r) => {
    if (filterAdminId && r.adminId !== parseInt(filterAdminId, 10)) return false;
    if (filterDateFrom && r.issueDate < filterDateFrom) return false;
    if (filterDateTo && r.issueDate > filterDateTo) return false;
    return true;
  });

  const filterSummary = useMemo(() => {
    const toNum = (v) => {
      if (v === null || v === undefined || v === '') return 0;
      const n = typeof v === 'number' ? v : parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    };
    return filteredRecords.reduce(
      (acc, r) => {
        acc.count += 1;
        acc.totalLost += toNum(r.actualMoneyLost);
        acc.totalDeduct += toNum(r.deductFromAdmin);
        return acc;
      },
      { count: 0, totalLost: 0, totalDeduct: 0 }
    );
  }, [filteredRecords]);

  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentRecords = filteredRecords.slice(indexOfFirstItem, indexOfLastItem);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'adminId') {
      setRecord((prev) => ({ ...prev, adminId: value, adminKpiRecordId: '' }));
      return;
    }
    if (name === 'orderUrl') {
      setRecord((prev) => ({ ...prev, orderUrl: value, orderId: prev.orderId }));
      setOrderResolveOptions([]);
      setSelectedOrder(null);
      return;
    }
    setRecord((prev) => ({ ...prev, [name]: value }));
  };

  const handleResolveOrderUrl = async () => {
    const url = String(record.orderUrl || '').trim();
    if (!url) {
      toast.error('Please input URL');
      return;
    }
    setOrderResolveLoading(true);
    try {
      const res = await AdminMistakeService.resolveOrderByUrl(url);
      const data = res.data || {};
      if (data.status === 'single' && data.orderId) {
        setOrderResolveOptions([]);
        setRecord((prev) => ({ ...prev, orderId: String(data.orderId) }));
        setSelectedOrder(data.order || null);
        toast.success('Order found and linked');
        return;
      }
      if (data.status === 'multiple' && Array.isArray(data.orders) && data.orders.length > 0) {
        setOrderResolveOptions(data.orders);
        setSelectedOrder(null);
        toast.info('Multiple orders found — please select one');
        return;
      }
      setOrderResolveOptions([]);
      setSelectedOrder(null);
      toast.error('No order found for this URL');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to resolve URL');
    } finally {
      setOrderResolveLoading(false);
    }
  };

  const handleEdit = (id) => {
    const row = mistakes.find((r) => r.id === id);
    if (!row) return;
    setRecord({
      id: row.id,
      orderId: row.orderId != null ? String(row.orderId) : '',
      orderUrl: row.order?.url || '',
      adminId: row.adminId,
      adminKpiRecordId: row.adminKpiRecordId != null ? String(row.adminKpiRecordId) : '',
      actualMoneyLost: row.actualMoneyLost != null ? String(row.actualMoneyLost) : '',
      deductFromAdmin: row.deductFromAdmin != null ? String(row.deductFromAdmin) : '',
      reason: row.reason || '',
      issueDate: row.issueDate || '',
      notes: row.notes || ''
    });
    setOrderResolveOptions([]);
    setSelectedOrder(row.order ? {
      id: row.order.id,
      orderCode: row.order.orderCode,
      customerId: row.order.customerId,
      url: row.order.url,
      smileNameInput: row.order.smileNameInput
    } : null);
    setMode('edit');
  };

  const handleResetForm = () => {
    setRecord(emptyForm());
    setOrderResolveOptions([]);
    setSelectedOrder(null);
    setMode('register');
  };

  const openConfirm = (id) => {
    setDeleteTargetId(id);
    setIsConfirmOpen(true);
  };

  const closeConfirm = () => {
    setDeleteTargetId(null);
    setIsConfirmOpen(false);
  };

  const payloadFromForm = () => ({
    orderId: record.orderId === '' ? null : record.orderId,
    adminId: parseInt(record.adminId, 10),
    adminKpiRecordId: record.adminKpiRecordId === '' ? null : record.adminKpiRecordId,
    actualMoneyLost: record.actualMoneyLost === '' ? 0 : parseFloat(record.actualMoneyLost) || 0,
    deductFromAdmin: record.deductFromAdmin === '' ? 0 : parseFloat(record.deductFromAdmin) || 0,
    reason: record.reason,
    issueDate: record.issueDate,
    notes: record.notes || null
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const body = payloadFromForm();
      if (!body.adminId) {
        toast.error('Please select an admin');
        return;
      }
      if (mode === 'edit') {
        await editMistake(record.id, body);
        toast.success('Record updated successfully!');
        handleResetForm();
      } else {
        const ok = await addMistake(body);
        if (ok) {
          toast.success('Record created successfully!');
          handleResetForm();
        } else {
          toast.error(error || 'Failed to create');
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'An unexpected error occurred.');
    }
  };

  const handleDelete = async () => {
    if (deleteTargetId == null) return;
    try {
      await deleteMistake(deleteTargetId);
      toast.success('Record deleted successfully!');
      closeConfirm();
    } catch (err) {
      toast.error(err.response?.data?.message || 'An unexpected error occurred.');
    }
  };

  useEffect(() => {
    if (error && typeof error === 'string') {
      toast.error(error);
    }
  }, [error]);

  useEffect(() => {
    refreshMistakes();
  }, []);

  return (
    <div className="px-4 sm:px-6 lg:px-8 h-full">
      <div className="mt-4">
        <h2 className="text-5xl mb-4">Admin mistake records</h2>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <div className="bg-white p-4 rounded-lg shadow mb-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Filter by admin</label>
                  <select
                    value={filterAdminId}
                    onChange={(e) => setFilterAdminId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">All admins</option>
                    {admins.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.nickname || `Admin #${a.id}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Issue date from</label>
                  <input
                    type="date"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Issue date to</label>
                  <input
                    type="date"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <p className="text-xs text-gray-500">Filtered records</p>
                  <p className="text-xl font-semibold text-gray-900">{filterSummary.count}</p>
                </div>
                <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                  <p className="text-xs text-red-700">Total lost</p>
                  <p className="text-xl font-semibold text-red-900">{filterSummary.totalLost.toFixed(2)}</p>
                </div>
                <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
                  <p className="text-xs text-indigo-700">Total deduction</p>
                  <p className="text-xl font-semibold text-indigo-900">{filterSummary.totalDeduct.toFixed(2)}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">No</th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Admin</th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">KPI link</th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Lost</th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Deduct</th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Issue date</th>
                      <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {isLoading ? (
                      <tr>
                        <td colSpan="9" className="py-4 text-center text-gray-500">Loading...</td>
                      </tr>
                    ) : currentRecords.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="py-4 text-center text-gray-500">No records found</td>
                      </tr>
                    ) : (
                      currentRecords.map((r, index) => (
                        <tr key={r.id} className="hover:bg-gray-50">
                          <td className="py-3 px-4 text-sm text-gray-900">{indexOfFirstItem + index + 1}</td>
                          <td className="py-3 px-4 text-sm text-gray-500">
                            {r.admin?.nickname || `Admin #${r.adminId}`}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-500">
                            {r.order?.smileNameInput || r.order?.orderCode || (r.orderId ? `#${r.orderId}` : '—')}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-500 max-w-[140px]">
                            {r.kpiRecord ? (
                              <span className="line-clamp-2" title={`KPI #${r.kpiRecord.id}`}>
                                #{r.kpiRecord.id}{' '}
                                {r.kpiRecord.kpi?.topic || r.kpiRecord.kpi?.group || ''}
                                <span className="text-gray-400">
                                  {' '}
                                  ({r.kpiRecord.month}/{r.kpiRecord.year})
                                </span>
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-500">{r.actualMoneyLost}</td>
                          <td className="py-3 px-4 text-sm text-gray-500">{r.deductFromAdmin}</td>
                          <td className="py-3 px-4 text-sm text-gray-500 max-w-xs truncate" title={r.reason}>
                            {r.reason}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-500 whitespace-nowrap">{r.issueDate}</td>
                          <td className="py-3 px-4 text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <PencilIcon
                                onClick={() => handleEdit(r.id)}
                                className="cursor-pointer w-5 h-5 text-green-600 hover:text-green-800"
                              />
                              <TrashIcon
                                onClick={() => openConfirm(r.id)}
                                className="cursor-pointer w-5 h-5 text-red-600 hover:text-red-800"
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(indexOfLastItem, filteredRecords.length)}</span> of{' '}
                    <span className="font-medium">{filteredRecords.length}</span>
                  </p>
                  <nav className="inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm text-gray-500 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    {[...Array(totalPages)].map((_, idx) => {
                      const page = idx + 1;
                      if (page === 1 || page === totalPages || (page >= currentPage - 2 && page <= currentPage + 2)) {
                        return (
                          <button
                            key={page}
                            type="button"
                            onClick={() => setCurrentPage(page)}
                            className={`px-4 py-2 border text-sm ${
                              currentPage === page
                                ? 'bg-indigo-50 border-indigo-500 text-indigo-600'
                                : 'bg-white border-gray-300 text-gray-500'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      }
                      if (page === currentPage - 3 || page === currentPage + 3) {
                        return <span key={page} className="px-4 py-2 border border-gray-300 bg-white text-sm">...</span>;
                      }
                      return null;
                    })}
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm text-gray-500 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-1">
            <div
              className={`bg-white rounded-lg shadow p-6 sticky top-4 border-2 ${
                mode === 'edit' ? 'border-yellow-400' : 'border-green-400'
              }`}
            >
              <div
                className={`mb-4 p-3 rounded-md ${
                  mode === 'edit' ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'
                }`}
              >
                <h3 className={`text-2xl font-bold ${mode === 'edit' ? 'text-yellow-800' : 'text-green-800'}`}>
                  {mode === 'edit' ? 'Edit mistake record' : 'Record new mistake'}
                </h3>
                <p className={`text-sm mt-1 ${mode === 'edit' ? 'text-yellow-700' : 'text-green-700'}`}>
                  {mode === 'edit' ? 'Update an existing entry' : 'Log loss and admin deduction'}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Admin *</label>
                  <select
                    name="adminId"
                    value={record.adminId}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select admin</option>
                    {admins.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.nickname || `Admin #${a.id}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Order URL (optional)</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      name="orderUrl"
                      value={record.orderUrl}
                      onChange={handleInputChange}
                      placeholder="Paste order URL"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={handleResolveOrderUrl}
                      disabled={orderResolveLoading || !String(record.orderUrl || '').trim()}
                      className="px-3 py-2 bg-gray-900 text-white rounded-md disabled:opacity-50"
                    >
                      {orderResolveLoading ? 'Resolving...' : 'Resolve'}
                    </button>
                  </div>

                  {orderResolveOptions.length > 0 && (
                    <div className="mt-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Multiple matches — select which `smileNameInput` / order to link
                      </label>
                      <select
                        value={record.orderId}
                        onChange={(e) => {
                          const val = e.target.value;
                          setRecord((prev) => ({ ...prev, orderId: val }));
                          const match = orderResolveOptions.find((o) => String(o.id) === String(val));
                          setSelectedOrder(match || null);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="">Select order</option>
                        {orderResolveOptions.map((o) => (
                          <option key={o.id} value={String(o.id)}>
                            {o.smileNameInput ? o.smileNameInput : '(no smileNameInput)'} — {o.orderCode ? o.orderCode : `#${o.id}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="mt-2 flex items-center justify-between">
                    <p className="text-xs text-gray-500">
                      Linked Order: {selectedOrderLabel}
                    </p>
                    {record.orderId && (
                      <button
                        type="button"
                        onClick={() => {
                          setRecord((prev) => ({ ...prev, orderId: '' }));
                          setSelectedOrder(null);
                        }}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Clear link
                      </button>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Linked KPI record (optional)
                  </label>
                  <select
                    name="adminKpiRecordId"
                    value={record.adminKpiRecordId}
                    onChange={handleInputChange}
                    disabled={!record.adminId}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:text-gray-500"
                  >
                    <option value="">None</option>
                    {kpiSelectOptions.map((k) => (
                      <option key={k.id} value={k.id}>
                        #{k.id} — {k.kpi?.topic || k.kpi?.group || 'KPI'} ({k.month}/{k.year},{' '}
                        {k.pointsDeducted} pts)
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Choose an admin first. Only KPI rows for that admin are listed; must match the mistake admin.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Actual money lost</label>
                  <input
                    type="number"
                    name="actualMoneyLost"
                    value={record.actualMoneyLost}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deduct from admin</label>
                  <input
                    type="number"
                    name="deductFromAdmin"
                    value={record.deductFromAdmin}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
                  <input
                    type="text"
                    name="reason"
                    value={record.reason}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Issue date *</label>
                  <input
                    type="date"
                    name="issueDate"
                    value={record.issueDate}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    name="notes"
                    value={record.notes}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div className="flex space-x-2">
                  <button
                    type="submit"
                    className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {mode === 'edit' ? 'Update' : 'Create'}
                  </button>
                  {mode === 'edit' && (
                    <button
                      type="button"
                      onClick={handleResetForm}
                      className="flex-1 bg-gray-400 text-white px-4 py-2 rounded-md hover:bg-gray-500"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <Modal isOpen={isConfirmOpen} onRequestClose={closeConfirm} style={customStyles} ariaHideApp={false}>
        <DeleteConfirm
          items={[]}
          data={record}
          closeConfirm={closeConfirm}
          confirmFunction={handleDelete}
          titleText="Are you sure you want to delete this mistake record?"
        />
      </Modal>
    </div>
  );
};

export default AdminMistakeRecord;
